import { NetworkStatus, QueryResult, gql, useQuery } from '@apollo/client';
import * as React from 'react';

import {
  EAttribute_InputType,
  MemberCompanyDirectoryAttributeFilterInput,
} from '@/graphql-types/globalTypes';
import { useDebouncedValue } from '@/imports/ui/hooks/useDebouncedValue';

import { MemberCompanyDirectory_FilterOptions } from './graphql-types/MemberCompanyDirectory_FilterOptions';
import {
  MemberCompanyDirectory_Search,
  MemberCompanyDirectory_SearchVariables,
} from './graphql-types/MemberCompanyDirectory_Search';

import {
  MEMBER_COMPANY_DIRECTORY_FREE_TEXT_SEARCH_DEBOUNCE_MS,
  MEMBER_COMPANY_DIRECTORY_PAGE_SIZE,
  MEMBER_COMPANY_DIRECTORY_SEARCH_DEBOUNCE_MS,
} from '../constants';
import { MemberCompanyDirectoryFilterOptions } from '../types';
import {
  buildMemberCompanyDirectorySearchFilters,
  isFreeTextCompanyDirectoryFilter,
  useMemberCompanyDirectoryFilters,
} from './useMemberCompanyDirectoryFilters';

const parseAttributeFiltersKey = (
  attributeFiltersKey: string
): MemberCompanyDirectoryAttributeFilterInput[] | null => {
  const parsed: unknown = JSON.parse(attributeFiltersKey);
  return parsed as MemberCompanyDirectoryAttributeFilterInput[] | null;
};

export const MEMBER_COMPANY_DIRECTORY_SEARCH = gql`
  query MemberCompanyDirectory_Search(
    $input: MemberCompanyDirectorySearchInput!
  ) {
    memberCompanyDirectorySearch(input: $input) {
      totalCount
      results {
        _id
        displayName
        aboutUsPlainText
        profilePictureUrl
        company {
          website
          phoneNumber
          address {
            streetAddress
            city
            state
            country
            zipCode
          }
        }
        cardAttributes {
          attributeSchemaId
          name
          displayValues
        }
      }
    }
  }
`;

export const MEMBER_COMPANY_DIRECTORY_FILTER_OPTIONS = gql`
  query MemberCompanyDirectory_FilterOptions {
    memberCompanyDirectoryFilterOptions {
      attributeFilters {
        _id
        name
        inputType
        options {
          label
        }
      }
    }
  }
`;

export type UseMemberCompanyDirectorySearchResult = {
  isLoading: boolean;
  isInitialLoading: boolean;
  searchText: string;
  setSearchText: (value: string) => void;
  freeTextValues: Record<string, string>;
  setFreeTextValue: (attributeSchemaId: string, value: string) => void;
  page: number;
  setPage: (page: number) => void;
  limit: number;
  results: MemberCompanyDirectory_Search['memberCompanyDirectorySearch']['results'];
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  selectedFilters: ReturnType<
    typeof useMemberCompanyDirectoryFilters
  >['selectedFilters'];
  unselectedFilters: ReturnType<
    typeof useMemberCompanyDirectoryFilters
  >['unselectedFilters'];
  selectFilter: ReturnType<
    typeof useMemberCompanyDirectoryFilters
  >['selectFilter'];
  unselectFilter: (params: { attributeSchemaId: string }) => void;
  isLoadingFilters: boolean;
  someFilterIsApplied: boolean;
  isSearchDebouncing: boolean;
  isFreeTextSearchDebouncing: boolean;
  isSearchQueryFetching: boolean;
  memberCompanyDirectorySearch: Pick<
    QueryResult<
      MemberCompanyDirectory_Search,
      MemberCompanyDirectory_SearchVariables
    >,
    'data' | 'networkStatus' | 'error' | 'loading'
  >;
  refetch: () => void;
};

export const useMemberCompanyDirectorySearch =
  (): UseMemberCompanyDirectorySearchResult => {
    const [searchText, setSearchText] = React.useState('');
    const [freeTextValues, setFreeTextValues] = React.useState<
      Record<string, string>
    >({});
    const [page, setPage] = React.useState(1);
    const [accumulatedResults, setAccumulatedResults] = React.useState<
      MemberCompanyDirectory_Search['memberCompanyDirectorySearch']['results']
    >([]);
    const [shouldLoadFilterOptions, setShouldLoadFilterOptions] =
      React.useState(false);

    const debouncedSearchText = useDebouncedValue(
      searchText,
      MEMBER_COMPANY_DIRECTORY_SEARCH_DEBOUNCE_MS
    );
    const debouncedFreeTextValues = useDebouncedValue(
      freeTextValues,
      MEMBER_COMPANY_DIRECTORY_FREE_TEXT_SEARCH_DEBOUNCE_MS
    );

    const filterOptionsQuery = useQuery<MemberCompanyDirectory_FilterOptions>(
      MEMBER_COMPANY_DIRECTORY_FILTER_OPTIONS,
      {
        skip: !shouldLoadFilterOptions,
        fetchPolicy: 'cache-first',
      }
    );

    const filterOptions: MemberCompanyDirectoryFilterOptions | undefined =
      filterOptionsQuery.data?.memberCompanyDirectoryFilterOptions
        ? {
            attributeFilters:
              filterOptionsQuery.data.memberCompanyDirectoryFilterOptions.attributeFilters.map(
                (attributeFilter) => ({
                  _id: attributeFilter._id,
                  name: attributeFilter.name,
                  inputType: attributeFilter.inputType as EAttribute_InputType,
                  options: attributeFilter.options.map((option) => ({
                    label: option.label,
                  })),
                })
              ),
          }
        : undefined;

    const {
      selectedFilters,
      unselectedFilters,
      selectFilter,
      unselectFilter: unselectFilterBase,
      isLoadingFilters,
      someSelectFilterApplied,
    } = useMemberCompanyDirectoryFilters(
      filterOptions,
      shouldLoadFilterOptions && filterOptionsQuery.loading
    );

    const setFreeTextValue = React.useCallback(
      (attributeSchemaId: string, value: string) => {
        setFreeTextValues((previous) => ({
          ...previous,
          [attributeSchemaId]: value,
        }));
      },
      []
    );

    const unselectFilter = React.useCallback(
      (params: { attributeSchemaId: string }) => {
        unselectFilterBase(params);
        setFreeTextValues((previous) => {
          if (!(params.attributeSchemaId in previous)) {
            return previous;
          }
          const next = { ...previous };
          delete next[params.attributeSchemaId];
          return next;
        });
      },
      [unselectFilterBase]
    );

    const searchFilterInput = React.useMemo(
      () =>
        buildMemberCompanyDirectorySearchFilters(
          selectedFilters,
          debouncedFreeTextValues
        ),
      [selectedFilters, debouncedFreeTextValues]
    );

    const someFreeTextFilterApplied = selectedFilters.some(
      (filter) =>
        isFreeTextCompanyDirectoryFilter(filter) &&
        Boolean(debouncedFreeTextValues[filter.attributeSchema._id]?.trim())
    );

    const someFilterIsApplied =
      someSelectFilterApplied || someFreeTextFilterApplied;

    const attributeFiltersKey = React.useMemo(
      () => JSON.stringify(searchFilterInput.attributeFilters ?? null),
      [searchFilterInput]
    );

    const attributeFilters = React.useMemo(
      () => parseAttributeFiltersKey(attributeFiltersKey),
      [attributeFiltersKey]
    );

    React.useEffect(() => {
      setPage(1);
      setAccumulatedResults([]);
    }, [debouncedSearchText, attributeFiltersKey]);

    const searchVariables =
      React.useMemo<MemberCompanyDirectory_SearchVariables>(
        () => ({
          input: {
            searchText: debouncedSearchText === '' ? null : debouncedSearchText,
            attributeFilters,
            limit: MEMBER_COMPANY_DIRECTORY_PAGE_SIZE,
            offset: (page - 1) * MEMBER_COMPANY_DIRECTORY_PAGE_SIZE,
          },
        }),
        [attributeFilters, debouncedSearchText, page]
      );

    const { refetch, ...memberCompanyDirectorySearch } = useQuery<
      MemberCompanyDirectory_Search,
      MemberCompanyDirectory_SearchVariables
    >(MEMBER_COMPANY_DIRECTORY_SEARCH, {
      variables: searchVariables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-first',
    });

    React.useEffect(() => {
      if (
        memberCompanyDirectorySearch.data ||
        memberCompanyDirectorySearch.error
      ) {
        setShouldLoadFilterOptions(true);
      }
    }, [memberCompanyDirectorySearch.data, memberCompanyDirectorySearch.error]);

    const searchData =
      memberCompanyDirectorySearch.networkStatus === NetworkStatus.setVariables
        ? memberCompanyDirectorySearch.previousData
            ?.memberCompanyDirectorySearch
        : memberCompanyDirectorySearch.data?.memberCompanyDirectorySearch;

    const results = React.useMemo(
      () => searchData?.results ?? [],
      [searchData?.results]
    );
    const totalCount = searchData?.totalCount ?? 0;
    const totalPages = Math.max(
      1,
      Math.ceil(totalCount / MEMBER_COMPANY_DIRECTORY_PAGE_SIZE)
    );

    React.useEffect(() => {
      if (
        memberCompanyDirectorySearch.networkStatus ===
        NetworkStatus.setVariables
      ) {
        return;
      }

      if (page === 1) {
        setAccumulatedResults(results);
        return;
      }

      setAccumulatedResults((previous) => {
        const existingIds = new Set(previous.map((company) => company._id));
        const newCompanies = results.filter(
          (company) => !existingIds.has(company._id)
        );
        return [...previous, ...newCompanies];
      });
    }, [memberCompanyDirectorySearch.networkStatus, page, results]);

    React.useEffect(() => {
      if (page > totalPages) {
        setPage(1);
      }
    }, [page, totalPages]);

    const isLoading =
      memberCompanyDirectorySearch.loading ||
      memberCompanyDirectorySearch.networkStatus === NetworkStatus.setVariables;

    const isInitialLoading =
      memberCompanyDirectorySearch.loading && results.length === 0;

    const isSearchDebouncing =
      searchText !== '' && searchText !== debouncedSearchText;

    const freeTextValuesKey = JSON.stringify(freeTextValues);
    const debouncedFreeTextValuesKey = JSON.stringify(debouncedFreeTextValues);
    const isFreeTextSearchDebouncing =
      freeTextValuesKey !== debouncedFreeTextValuesKey;

    const isSearchQueryFetching =
      memberCompanyDirectorySearch.networkStatus ===
        NetworkStatus.setVariables &&
      (debouncedSearchText !== '' || someFreeTextFilterApplied);

    const hasMore = page < totalPages;

    const loadMore = React.useCallback(() => {
      if (page < totalPages && !isLoading) {
        setPage((currentPage) => currentPage + 1);
      }
    }, [page, totalPages, isLoading]);

    const isLoadingMore = isLoading && accumulatedResults.length > 0;

    return {
      isLoading,
      isSearchQueryFetching,
      isInitialLoading,
      searchText,
      setSearchText,
      freeTextValues,
      setFreeTextValue,
      page,
      setPage,
      limit: MEMBER_COMPANY_DIRECTORY_PAGE_SIZE,
      results: accumulatedResults,
      totalCount,
      totalPages,
      hasMore,
      isLoadingMore,
      loadMore,
      selectedFilters,
      unselectedFilters,
      selectFilter,
      unselectFilter,
      isLoadingFilters,
      someFilterIsApplied,
      isSearchDebouncing,
      isFreeTextSearchDebouncing,
      memberCompanyDirectorySearch,
      refetch: () => {
        refetch();
      },
    };
  };
